/**
 * The navigation bar: the hamburger menu below the breakpoint, and the
 * dropdowns inside it.
 *
 * `navigation/_navigation.html.haml` renders the one `[data-topbar]` in the
 * app, and `vendor/assets/stylesheets/foundation-customized/components/_top-bar.scss`
 * lays it out. The stylesheet is what actually moves the bar; this module only
 * supplies the state it keys off — `expanded` on the bar, `moved` on an item
 * drilled into, `hover` on an open dropdown, `not-click` to hand hovering over
 * to CSS — plus the offsets that cannot be expressed as classes, because the
 * submenu slides by a multiple of the section's own width.
 *
 * Dropdowns open on hover above the breakpoint through `not-click`, so the
 * press handler here is for touch devices, which have no hover to offer.
 */

import { delegate } from 'common/delegate';
import satisfiesMediaQuery from 'utils/satisfiesMediaQuery';

const TOPBAR_SELECTOR = '[data-topbar]';
const SECTION_SELECTOR = 'section, .top-bar-section';

/** The `.top-bar-section` slide — `$topbar-transition-speed` in `_top-bar.scss`. */
const BACK_TRANSITION_MS = 300;

/** One level per dropdown drilled into, and the bar's own height in pixels. */
const drillDepths = new WeakMap<HTMLElement, number>();
const barHeights = new WeakMap<HTMLElement, number>();

/** The delayed unmark of the level a back press is sliding away from. */
const pendingUnmarks = new WeakMap<
  HTMLElement,
  { handle: number; item: HTMLElement }
>();

let listenersBound = false;

export default function initializeTopbar(): void {
  document
    .querySelectorAll<HTMLElement>(TOPBAR_SELECTOR)
    .forEach(bar => assemble(bar));

  if (listenersBound) return;
  listenersBound = true;

  document.addEventListener('click', closeOpenDropdowns);
  delegate(document, 'click', TOPBAR_SELECTOR, handleBarClick);
  // A long-press on a touch screen raises `contextmenu` rather than `click`,
  // and an open dropdown has to answer it too, or it stays open behind the
  // native menu.
  delegate(document, 'contextmenu', TOPBAR_SELECTOR, handleBarContextMenu);
  window.addEventListener('resize', handleResize);
}

function isAboveBreakpoint(): boolean {
  return satisfiesMediaQuery('topbar');
}

/**
 * Read from the global rather than imported: webpack lists Modernizr as an
 * external, so an import binds whatever object existed when this module first
 * loaded.
 */
function isTouchDevice(): boolean {
  const { Modernizr } = window as unknown as {
    Modernizr?: { touch?: boolean };
  };
  return Boolean(Modernizr?.touch);
}

/** `data-options="back_text: Takaisin"` — semicolon-separated `key: value`. */
function readBackText(bar: HTMLElement): string {
  const parsed = new Map<string, string>();

  (bar.dataset.options ?? '').split(';').forEach(pair => {
    const separator = pair.indexOf(':');
    if (separator === -1) return;
    const key = pair.slice(0, separator).trim();
    if (key) parsed.set(key, pair.slice(separator + 1).trim());
  });

  return parsed.get('back_text') ?? 'Back';
}

function outerHeight(element: HTMLElement): number {
  const { marginTop, marginBottom } = getComputedStyle(element);
  return (
    element.offsetHeight +
    (Number.parseFloat(marginTop) || 0) +
    (Number.parseFloat(marginBottom) || 0)
  );
}

function sectionOf(bar: HTMLElement): HTMLElement | null {
  return bar.querySelector<HTMLElement>(SECTION_SELECTOR);
}

/**
 * Gives every dropdown a way back out on a touch screen: the drill-down hides
 * the level above, so without these the only exits are closing the whole menu
 * or reaching the parent link, which the level itself no longer shows.
 */
function assemble(bar: HTMLElement): void {
  const backText = readBackText(bar);
  barHeights.set(bar, bar.offsetHeight);
  drillDepths.set(bar, 0);

  bar.querySelectorAll<HTMLElement>('.has-dropdown').forEach(item => {
    item.classList.add('not-click');
  });

  bar.querySelectorAll<HTMLElement>('.has-dropdown > a').forEach(link => {
    const dropdown =
      link.parentElement?.querySelector<HTMLElement>(':scope > .dropdown');
    if (!dropdown || dropdown.querySelector('.title.back')) return;

    const back = document.createElement('li');
    back.className = 'title back js-generated';
    const backLink = document.createElement('a');
    backLink.className = 'js-back';
    // An anchor with no `href` is not tabbable, and `#` would match the
    // dismissal that closes the whole menu on a link going nowhere — this
    // link steps back one level instead.
    // eslint-disable-next-line no-script-url
    backLink.href = 'javascript:void(0)';
    backLink.textContent = backText;
    back.appendChild(backLink);
    dropdown.prepend(back);

    const href = link.getAttribute('href');
    if (!href) return;

    const parent = document.createElement('li');
    parent.className = 'parent-link';
    const parentLink = document.createElement('a');
    parentLink.className = 'parent-link js-generated';
    parentLink.href = href;
    parentLink.innerHTML = link.innerHTML;
    parent.appendChild(parentLink);
    back.after(parent);
  });

  bar.querySelectorAll<HTMLElement>('.dropdown a').forEach(link => {
    link.addEventListener('focus', handleDropdownFocusChange);
    link.addEventListener('blur', handleDropdownFocusChange);
  });
}

/**
 * Slides the section back to the top level and drops the marks the drill-down
 * left, so the menu opens where it was last closed rather than part-way into a
 * dropdown.
 */
function toggleMenu(bar: HTMLElement): void {
  if (!isAboveBreakpoint()) {
    flushPendingUnmark(bar);
    const section = sectionOf(bar);
    if (section) section.style.left = '0%';
    bar
      .querySelectorAll('li.moved')
      .forEach(item => item.classList.remove('moved'));
    drillDepths.set(bar, 0);

    bar.classList.toggle('expanded');
    bar.style.height = '';
  }

  syncSidebar(bar);
}

/**
 * The dashboard sidebar sits outside the bar and slides with it. It is a
 * sibling rather than a child, so nothing in the stylesheet can tie the two
 * together.
 */
function syncSidebar(bar: HTMLElement): void {
  const expanded = bar.classList.contains('expanded');

  document.querySelectorAll('.m-navigation').forEach(sidebar => {
    sidebar.classList.toggle('m-navigation--expanded', expanded);
  });

  // The Tailwind sidebar (app/views/layouts/dashboard_navigation/tailwind)
  // shows itself on narrow screens when data-expanded is set.
  document.querySelectorAll('[data-tailwind-sidebar]').forEach(sidebar => {
    sidebar.toggleAttribute('data-expanded', expanded);
  });
}

function closeDropdowns(within: ParentNode): void {
  within
    .querySelectorAll('li.hover')
    .forEach(item => item.classList.remove('hover'));
}

/**
 * Anywhere but inside an open dropdown closes it — the page behind, and the
 * rest of the bar alike, so the logo does not leave one hanging open.
 */
function closeOpenDropdowns(event: MouseEvent): void {
  if (!(event.target instanceof Element)) return;
  if (event.target.closest('li')?.closest('li.hover')) return;
  document
    .querySelectorAll(TOPBAR_SELECTOR)
    .forEach(bar => closeDropdowns(bar));
}

function handleBarClick(event: MouseEvent, bar: Element): void {
  if (!(bar instanceof HTMLElement)) return;
  if (!(event.target instanceof Element)) return;
  const { target } = event;

  const toggle = target.closest('.toggle-topbar');
  if (toggle && bar.contains(toggle)) {
    event.preventDefault();
    toggleMenu(bar);
    return;
  }

  if (handleSectionLink(bar, target)) return;
  if (handleDropdownPress(event, bar, target)) return;
  handleDrillDown(event, bar, target);
}

function handleBarContextMenu(event: MouseEvent, bar: Element): void {
  if (!(bar instanceof HTMLElement)) return;
  if (!(event.target instanceof Element)) return;
  handleSectionLink(bar, event.target);
}

/**
 * A link that goes nowhere on its own has to close the menu itself, or the
 * bar stays over the page it just revealed. A link with a real path is left
 * alone: the page it loads renders the bar afresh.
 */
function handleSectionLink(bar: HTMLElement, target: Element): boolean {
  const link = target.closest('.top-bar-section li a[href^="#"]');
  if (!link) return false;

  closeDropdowns(bar);

  const item = link.closest('li');
  if (!isAboveBreakpoint() && !item?.classList.contains('has-dropdown')) {
    toggleMenu(bar);
    return true;
  }

  return false;
}

/**
 * Above the breakpoint a pointer that can hover opens dropdowns through the
 * stylesheet, so only a touch screen needs the class set here.
 */
function handleDropdownPress(
  event: MouseEvent,
  bar: HTMLElement,
  target: Element
): boolean {
  const item = target.closest<HTMLElement>('li.has-dropdown');
  if (!item) return false;

  if (target.closest('[data-reveal-id]')) {
    toggleMenu(bar);
    return true;
  }

  if (!isAboveBreakpoint() || !isTouchDevice()) return false;

  event.stopImmediatePropagation();

  if (item.classList.contains('hover')) {
    item.classList.remove('hover');
    closeDropdowns(item);
    let ancestor = item.parentElement?.closest('li.hover');
    while (ancestor) {
      ancestor.classList.remove('hover');
      ancestor = ancestor.parentElement?.closest('li.hover') ?? null;
    }
  } else {
    item.classList.add('hover');
    Array.from(item.parentElement?.children ?? [])
      .filter(sibling => sibling !== item)
      .forEach(sibling => sibling.classList.remove('hover'));

    if (target.tagName === 'A' && target.parentElement === item) {
      event.preventDefault();
    }
  }

  return true;
}

/**
 * Below the breakpoint a dropdown is a level of its own rather than a panel
 * over the page, so opening one slides the whole section sideways by its own
 * width and grows the bar to the height of what is now showing.
 */
function handleDrillDown(
  event: MouseEvent,
  bar: HTMLElement,
  target: Element
): void {
  const link = target.closest<HTMLElement>('.has-dropdown > a');
  const backLink = target.closest<HTMLElement>('.has-dropdown .back a');

  if (backLink) {
    event.preventDefault();
    stepOut(bar, backLink);
    return;
  }

  if (!link || isAboveBreakpoint()) return;

  event.preventDefault();
  stepInto(bar, link);
}

function slideTo(bar: HTMLElement, depth: number): void {
  drillDepths.set(bar, depth);
  const section = sectionOf(bar);
  if (section) section.style.left = `${-100 * depth}%`;
}

function flushPendingUnmark(bar: HTMLElement): void {
  const pending = pendingUnmarks.get(bar);
  if (!pending) return;

  window.clearTimeout(pending.handle);
  pending.item.classList.remove('moved');
  pendingUnmarks.delete(bar);
}

function stepInto(bar: HTMLElement, link: HTMLElement): void {
  const item = link.closest<HTMLElement>('li');
  const submenu =
    link.parentElement?.querySelector<HTMLElement>(':scope > .dropdown');
  if (!item || !submenu) return;

  // A level still sliding away from a back press must finish leaving now, or
  // its unmark would land on whatever is drilled into next.
  flushPendingUnmark(bar);

  slideTo(bar, (drillDepths.get(bar) ?? 0) + 1);
  item.classList.add('moved');
  bar.style.height = `${outerHeight(submenu) + (barHeights.get(bar) ?? 0)}px`;
}

function stepOut(bar: HTMLElement, backLink: HTMLElement): void {
  flushPendingUnmark(bar);

  const moved = backLink.closest<HTMLElement>('li.moved');
  const previousLevel = moved?.parentElement;
  const depth = Math.max((drillDepths.get(bar) ?? 0) - 1, 0);

  slideTo(bar, depth);

  bar.style.height =
    depth === 0 || !previousLevel
      ? ''
      : `${outerHeight(previousLevel) + (barHeights.get(bar) ?? 0)}px`;

  // The level being left is still on screen for the length of the slide, and
  // unmarking it now would take it away mid-movement.
  if (moved) {
    pendingUnmarks.set(bar, {
      item: moved,
      handle: window.setTimeout(() => {
        moved.classList.remove('moved');
        pendingUnmarks.delete(bar);
      }, BACK_TRANSITION_MS)
    });
  }
}

/**
 * Keyboard access: a link inside a closed dropdown can still be tabbed to, so
 * focus opens every dropdown level above it, and losing focus closes them
 * again.
 */
function handleDropdownFocusChange(event: FocusEvent): void {
  const link = event.target;
  if (!(link instanceof Element)) return;

  const mark = event.type === 'focus';
  let item = link.closest('.has-dropdown');
  while (item) {
    item.classList.toggle('hover', mark);
    item = item.parentElement?.closest('.has-dropdown') ?? null;
  }
}

/**
 * Above the breakpoint the section is laid out along the bar and the hamburger
 * is hidden, so a menu left open on a narrower viewport would otherwise stay
 * expanded with no control able to close it. Resize fires continuously through
 * a window drag, so a bar already in its resting state is left unwritten.
 */
function handleResize(): void {
  if (!isAboveBreakpoint()) return;

  document.querySelectorAll<HTMLElement>(TOPBAR_SELECTOR).forEach(bar => {
    const wasExpanded = bar.classList.contains('expanded');
    if (!wasExpanded && !bar.style.height && !bar.querySelector('li.hover')) {
      return;
    }

    bar.style.height = '';
    bar.classList.remove('expanded');
    closeDropdowns(bar);

    if (wasExpanded) syncSidebar(bar);
  });
}
